/**
 * CDB-TQS API — ontology library (cdb_tqs#2189).
 *
 * System presets plus ontologies per client, declarative instead of Python.
 * Reading is open to admins (all) and supervisors (system + own client);
 * writing is admin-only. Errors keep cdb_tqs's `code` and, for 422
 * `ontology_invalid`, the `errors` list on `CdbTqsApiError`.
 */

import { CDB_TQS_API_BASE_URL, fetchWithLogging, type CdbTqsFieldError } from "./http";

export const ONTOLOGY_FIELD_TYPES = [
  "text",
  "integer",
  "number",
  "boolean",
  "text_list",
  "integer_list",
  "datetime",
  "date",
] as const;
export type OntologyFieldType = (typeof ONTOLOGY_FIELD_TYPES)[number];

export interface OntologyField {
  name: string;
  type: OntologyFieldType;
  description: string;
}

/** An entity type or an edge type — both have the same shape. */
export interface OntologyTypeDef {
  name: string;
  description: string;
  fields: OntologyField[];
}

/** Which edge types may connect `source` → `target` (entity type names). */
export interface OntologyEdgeTypeMapEntry {
  source: string;
  target: string;
  edges: string[];
}

export interface OntologyDefinition {
  entity_types: OntologyTypeDef[];
  edge_types: OntologyTypeDef[];
  edge_type_map: OntologyEdgeTypeMapEntry[];
}

/**
 * What cdb_tqs changed while importing Python (cdb_tqs#2190): `dropped_generic_entity`
 * (an empty `Entity` type) or `edge_type_opened` (`path` `edge_types[i]`, now allowed
 * between any entity types because it listed too many pairs for Graphiti's prompt).
 */
export interface OntologyImportNote {
  path: string;
  code: string;
  message: string;
}

export interface OntologyImport {
  definition: OntologyDefinition;
  notes: OntologyImportNote[];
}

/** `key` names the system presets; client ontologies have none. */
export type SystemOntologyKey = "allgemein" | "wissen_schulung" | "produkte_technik";

export interface OntologySummary {
  id: string;
  key: SystemOntologyKey | null;
  name: string;
  description: string | null;
  client_id: string | null;
  client_name: string | null;
  is_system: boolean;
  entity_type_count: number;
  edge_type_count: number;
  knowledge_area_count: number;
  updated_at: string;
}

export interface Ontology extends OntologySummary {
  definition: OntologyDefinition;
}

export interface OntologyListResponse {
  items: OntologySummary[];
  total: number;
}

export interface SaveOntologyPayload {
  name: string;
  description: string | null;
  client_id: string;
  definition: OntologyDefinition;
}

export interface OntologyValidation {
  valid: boolean;
  errors: CdbTqsFieldError[];
  stats: { entity_types: number; edge_types: number; edges: number };
}

const BASE = `${CDB_TQS_API_BASE_URL}/ontologies`;

export const ontologiesApi = {
  /** Admins get every ontology, supervisors the system ones and their client's. */
  async getOntologies(clientId?: string): Promise<OntologyListResponse> {
    const response = await fetchWithLogging(BASE, {
      method: "GET",
      ...(clientId ? { params: { client_id: clientId } } : {}),
    });
    return response.data as OntologyListResponse;
  },

  async getOntology(id: string): Promise<Ontology> {
    const response = await fetchWithLogging(`${BASE}/${encodeURIComponent(id)}`, { method: "GET" });
    return response.data as Ontology;
  },

  /** 422 `ontology_invalid` carries `errors` with their paths. */
  async createOntology(payload: SaveOntologyPayload): Promise<Ontology> {
    const response = await fetchWithLogging(BASE, { method: "POST", body: JSON.stringify(payload) });
    return response.data as Ontology;
  },

  /** 403 `ontology_system_readonly` for a system preset. */
  async updateOntology(id: string, payload: SaveOntologyPayload): Promise<Ontology> {
    const response = await fetchWithLogging(`${BASE}/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    return response.data as Ontology;
  },

  /** 409 `ontology_in_use` while knowledge areas use it. */
  async deleteOntology(id: string): Promise<void> {
    await fetchWithLogging(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  async validateOntology(definition: OntologyDefinition): Promise<OntologyValidation> {
    const response = await fetchWithLogging(`${BASE}/validate`, {
      method: "POST",
      body: JSON.stringify({ definition }),
    });
    return response.data as OntologyValidation;
  },

  /** Converts a Python ontology without executing it; `notes` lists what the import adapted. */
  async importPythonOntology(code: string): Promise<OntologyImport> {
    const response = await fetchWithLogging(`${BASE}/import-python`, {
      method: "POST",
      body: JSON.stringify({ code }),
    });
    const data = response.data as { definition: OntologyDefinition; notes?: OntologyImportNote[] };
    return { definition: data.definition, notes: data.notes ?? [] };
  },
};
